import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'motion/react';
import { useDirectorySearch } from '../hooks/useDirectorySearch';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Box,
  Typography,
  Avatar,
  Chip,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import {
  Group as GroupIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import apiService from '../services/api';

const AccountOwnersModal = ({ open, onClose, account, onSuccess, users = [], bulkAccountIds = [] }) => {
  const [owners, setOwners] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isBulkMode = bulkAccountIds.length > 0;

  useEffect(() => {
    if (open && account) {
      // Initialize owners from account data
      const existingOwners = account.owner_emails || account.ownerEmails || [];
      setOwners(Array.isArray(existingOwners) ? existingOwners : []);
      setSelectedUser(null);
      setInputValue('');
      setError('');
    }
  }, [open, account]);

  // Typing searches Keycloak, so an owner can be picked from the whole realm
  // rather than only the page the parent preloaded.
  const userSearch = useDirectorySearch('users', inputValue, { enabled: open });

  // Filter out users who are already owners
  const availableUsers = useMemo(() => {
    const source = inputValue.trim() ? userSearch.results : users;
    return source.filter((user) => {
      const email = user.email?.toLowerCase();
      return email && !owners.includes(email);
    });
  }, [users, owners, inputValue, userSearch.results]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddUser = () => {
    if (selectedUser) {
      const email = selectedUser.email?.toLowerCase();
      if (email && !owners.includes(email)) {
        setOwners([...owners, email]);
        setSelectedUser(null);
        setInputValue('');
        setError('');
      }
    } else if (inputValue.trim()) {
      // Allow adding custom email if no user selected
      const trimmedEmail = inputValue.trim().toLowerCase();

      if (!validateEmail(trimmedEmail)) {
        setError('Please enter a valid email address');
        return;
      }

      if (owners.includes(trimmedEmail)) {
        setError('This email is already an owner');
        return;
      }

      setOwners([...owners, trimmedEmail]);
      setInputValue('');
      setError('');
    }
  };

  const handleRemoveOwner = (emailToRemove) => {
    setOwners(owners.filter((email) => email !== emailToRemove));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      e.preventDefault();
      handleAddUser();
    }
  };

  // Get display label for user option
  const getOptionLabel = (option) => {
    if (typeof option === 'string') return option;
    const name = [option.firstName, option.lastName].filter(Boolean).join(' ');
    return name ? `${name} (${option.email})` : option.email || '';
  };

  // Matching is done by Keycloak against username, email and name; re-filtering
  // here would only hide rows the server deliberately returned.
  const filterOptions = (options) => options;

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      if (isBulkMode) {
        // Bulk mode: update owners for multiple accounts
        let successCount = 0;
        let failCount = 0;

        for (const accountId of bulkAccountIds) {
          try {
            const response = await apiService.updateAccountOwners(accountId, owners);
            if (response && response.success) {
              successCount++;
            } else {
              failCount++;
            }
          } catch (err) {
            console.error(`Failed to update owners for account ${accountId}:`, err);
            failCount++;
          }
        }

        if (successCount > 0) {
          toast.success(`Successfully updated owners for ${successCount} account(s)`);
        }
        if (failCount > 0) {
          toast.error(`Failed to update owners for ${failCount} account(s)`);
        }

        onSuccess && onSuccess();
        onClose();
      } else {
        // Single account mode
        const response = await apiService.updateAccountOwners(
          account.accountId || account.account_id,
          owners
        );

        if (response && response.success) {
          toast.success('Account owners updated successfully');
          onSuccess && onSuccess();
          onClose();
        } else {
          throw new Error(response?.message || 'Failed to update account owners');
        }
      }
    } catch (err) {
      console.error('Failed to update account owners:', err);
      setError(err.message || 'Failed to update account owners');
      toast.error(err.message || 'Failed to update account owners');
    } finally {
      setLoading(false);
    }
  };

  if (!account) return null;

  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      fontFamily: 'Metropolis, sans-serif',
      backgroundColor: '#FFFFFF',
      '& fieldset': {
        borderColor: '#E5E5E5',
      },
      '&:hover fieldset': {
        borderColor: '#4698D3',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#4698D3',
      },
    },
    '& .MuiInputLabel-root': {
      fontFamily: 'Metropolis, sans-serif',
      color: '#666666',
      '&.Mui-focused': {
        color: '#4698D3',
      },
    },
    '& .MuiFormHelperText-root': {
      fontFamily: 'Metropolis, sans-serif',
      color: '#E74C3C',
    },
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          border: '1px solid #E5E5E5',
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: '#EBF5FB',
          borderBottom: '1px solid #E5E5E5',
          fontFamily: 'Bitter, Georgia, serif',
          fontWeight: 600,
          fontSize: '1.25rem',
          color: '#253E66',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          position: 'relative',
        }}
      >
        <Avatar sx={{ backgroundColor: '#4698D3', width: 40, height: 40 }}>
          <GroupIcon />
        </Avatar>
        {isBulkMode ? 'Set Owners for Multiple Accounts' : 'Manage Account Owners'}
        <IconButton
          onClick={onClose}
          disabled={loading}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            color: '#666666',
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E5E5',
            '&:hover': { backgroundColor: '#F9FBFD' },
            '&:disabled': { opacity: 0.5 },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 4, backgroundColor: '#FFFFFF' }}>
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
        {/* Account Info */}
        <Box sx={{ mb: 3 }}>
          {isBulkMode && (
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontSize: '0.8rem',
                color: '#999999',
                fontWeight: 500,
                mb: 0.5,
              }}
            >
              Selected Accounts
            </Typography>
          )}
          <Box
            sx={{
              backgroundColor: '#F9FBFD',
              borderRadius: '6px',
              p: 1.5,
              border: '1px solid #E5E5E5',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontSize: '0.9rem',
                color: '#253E66',
                fontWeight: 600,
              }}
            >
              {isBulkMode
                ? `${bulkAccountIds.length} accounts selected`
                : (account.accountName || account.name)}
            </Typography>
            {!isBulkMode && (
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  color: '#666666',
                }}
              >
                {account.accountId || account.account_id}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Info Text */}
        <Box
          sx={{
            backgroundColor: '#FFF8E1',
            borderRadius: '6px',
            p: 2,
            mb: 3,
            border: '1px solid #FFE082',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontSize: '0.85rem',
              color: '#F57F17',
            }}
          >
            {isBulkMode
              ? 'The owners you set will be applied to all selected accounts. This will replace any existing owners.'
              : 'Account owners can approve JIT (Just-In-Time) access requests for this account. If no owners are set, SSO admins will be able to approve requests as a fallback.'}
          </Typography>
        </Box>

        {/* Current Owners / New Owners for Bulk */}
        <Typography
          sx={{
            fontFamily: 'Metropolis, sans-serif',
            fontSize: '0.9rem',
            color: '#253E66',
            fontWeight: 600,
            mb: 1,
          }}
        >
          {isBulkMode ? `Owners to Set (${owners.length})` : `Current Owners (${owners.length})`}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mb: 3,
            minHeight: 40,
            p: owners.length > 0 ? 1.5 : 2,
            backgroundColor: '#F9FBFD',
            borderRadius: '6px',
            border: '1px solid #E5E5E5',
          }}
        >
          {owners.length === 0 ? (
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontSize: '0.85rem',
                color: '#999999',
                fontStyle: 'italic',
              }}
            >
              No owners assigned (SSO admins will approve JIT requests)
            </Typography>
          ) : (
            owners.map((email) => (
              <Chip
                key={email}
                icon={<PersonIcon sx={{ fontSize: 16 }} />}
                label={email}
                onDelete={() => handleRemoveOwner(email)}
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.85rem',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E5E5E5',
                  '& .MuiChip-deleteIcon': {
                    color: '#999999',
                    '&:hover': {
                      color: '#E74C3C',
                    },
                  },
                }}
              />
            ))
          )}
        </Box>

        {/* Add New Owner */}
        <Typography
          sx={{
            fontFamily: 'Metropolis, sans-serif',
            fontSize: '0.9rem',
            color: '#253E66',
            fontWeight: 600,
            mb: 1,
          }}
        >
          Add Owner
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Autocomplete
            fullWidth
            freeSolo
            options={availableUsers}
            value={selectedUser}
            inputValue={inputValue}
            onChange={(event, newValue) => {
              setSelectedUser(newValue);
              setError('');
            }}
            onInputChange={(event, newInputValue) => {
              setInputValue(newInputValue);
              setError('');
            }}
            getOptionLabel={getOptionLabel}
            filterOptions={filterOptions}
            loading={userSearch.loading}
            loadingText="Searching users..."
            disabled={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="Search users or enter email..."
                error={!!error}
                helperText={error}
                onKeyPress={handleKeyPress}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#999999', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={inputStyle}
              />
            )}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 1,
                  px: 2,
                  '&:hover': {
                    backgroundColor: '#EBF5FB',
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: '#4698D3',
                    fontSize: '0.8rem',
                  }}
                >
                  {(option.firstName?.[0] || option.username?.[0] || 'U').toUpperCase()}
                </Avatar>
                <Box>
                  <Typography
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      color: '#253E66',
                    }}
                  >
                    {[option.firstName, option.lastName].filter(Boolean).join(' ') || option.username}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontSize: '0.8rem',
                      color: '#666666',
                    }}
                  >
                    {option.email}
                  </Typography>
                </Box>
              </Box>
            )}
            noOptionsText={
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.85rem',
                  color: '#999999',
                  py: 1,
                }}
              >
                {inputValue ? 'No users found. Press Enter to add as custom email.' : 'Start typing to search users...'}
              </Typography>
            }
            ListboxProps={{
              sx: {
                maxHeight: 250,
                '& .MuiAutocomplete-option': {
                  fontFamily: 'Metropolis, sans-serif',
                },
              },
            }}
          />
          <Button
            onClick={handleAddUser}
            disabled={loading || (!selectedUser && !inputValue.trim())}
            variant="contained"
            sx={{
              backgroundColor: '#4698D3',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              minWidth: 80,
              '&:hover': {
                backgroundColor: '#3580B8',
              },
              '&.Mui-disabled': {
                backgroundColor: '#E5E5E5',
                color: '#999999',
              },
            }}
          >
            <AddIcon />
          </Button>
        </Box>
        </motion.div>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0, backgroundColor: '#FFFFFF', gap: 2 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 600,
            textTransform: 'none',
            color: '#666666',
            borderColor: '#E5E5E5',
            borderRadius: '6px',
            '&:hover': {
              borderColor: '#999999',
              backgroundColor: '#F9FBFD',
            },
            '&:disabled': {
              color: '#999999',
            },
          }}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <GroupIcon />}
          sx={{
            backgroundColor: '#4698D3',
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: '6px',
            px: 3,
            '&:hover': {
              backgroundColor: '#3580B8',
            },
            '&.Mui-disabled': {
              backgroundColor: '#E5E5E5',
              color: '#999999',
            },
          }}
        >
          {loading ? 'Saving...' : 'Save Owners'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountOwnersModal;
